/**
 * @file lib/git/git-vector-store.ts
 * @description Git対応のベクトルストア
 * @author GFTD Co., Ltd.
 */

import { Index } from 'usearch';
import { Document } from '@langchain/core/documents';
import { GitFileSystem } from './git-fs';
import { randomUUID } from 'crypto';

/**
 * Git対応ベクトルストアエントリー
 */
export interface GitVectorStoreEntry {
  id: string;
  vector: number[];
  document: Document;
  timestamp: number;
  commitSha?: string;
}

/**
 * Git検索結果
 */
export interface GitSearchResult {
  document: Document;
  score: number;
  id: string;
  commitSha?: string;
}

/**
 * Git対応のベクトルストアクラス
 * usearchとGitを組み合わせて、ベクトル検索データをGitリポジトリで管理
 */
export class GitVectorStore {
  private index: Index;
  private gitFs: GitFileSystem;
  private entries: Map<string, GitVectorStoreEntry>;
  private dimensions: number;
  private isInitialized = false;
  private vectorsDir = 'vectors';
  private metadataDir = 'metadata';
  private indexFile = 'index.json';

  constructor(gitFs: GitFileSystem, dimensions: number = 384) {
    this.gitFs = gitFs;
    this.dimensions = dimensions;
    this.entries = new Map();
    
    // usearchインデックスの初期化
    this.index = new Index(this.dimensions);
  }

  /**
   * ベクトルストアの初期化
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // GitFileSystemの初期化
      await this.gitFs.initialize();

      // 必要なディレクトリを作成
      await this.initializeDirectories();

      // 既存のインデックスを読み込み
      await this.loadExistingIndex();

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize GitVectorStore:', error);
      throw error;
    }
  }

  /**
   * 必要なディレクトリを初期化
   */
  private async initializeDirectories(): Promise<void> {
    try {
      // ディレクトリ構造を作成
      const dirs = [this.vectorsDir, this.metadataDir];
      
      for (const dir of dirs) {
        const exists = await this.gitFs.exists(dir);
        if (!exists) {
          await this.gitFs.writeFile(`${dir}/.gitkeep`, '');
        }
      }
    } catch (error) {
      console.error('Failed to initialize directories:', error);
      throw error;
    }
  }

  /**
   * 既存のインデックスを読み込み
   */
  private async loadExistingIndex(): Promise<void> {
    try {
      const indexExists = await this.gitFs.exists(this.indexFile);
      if (!indexExists) {
        return;
      }

      const indexData = await this.gitFs.readFile(this.indexFile);
      const indexInfo = JSON.parse(indexData);

      // エントリーを復元
      this.entries.clear();
      const entriesArray = indexInfo.entries || [];
      
      for (const entryData of entriesArray) {
        const entry: GitVectorStoreEntry = {
          id: entryData.id,
          vector: entryData.vector,
          document: new Document({
            pageContent: entryData.document.pageContent,
            metadata: entryData.document.metadata,
          }),
          timestamp: entryData.timestamp,
          commitSha: entryData.commitSha,
        };

        this.entries.set(entry.id, entry);

        // usearchインデックスに追加
        this.index.add(BigInt(this.entries.size - 1), new Float32Array(entry.vector));
      }

    } catch (error) {
      console.warn('Failed to load existing index:', error);
      // エラーが発生しても続行
    }
  }

  /**
   * ベクトルとドキュメントを追加
   * @param vector ベクトル
   * @param document ドキュメント
   * @returns エントリーID
   */
  async add(vector: number[], document: Document): Promise<string> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const id = randomUUID();
      const entry: GitVectorStoreEntry = {
        id,
        vector,
        document,
        timestamp: Date.now(),
      };

      // usearchインデックスに追加
      this.index.add(BigInt(this.entries.size), new Float32Array(vector));

      // エントリーを保存
      this.entries.set(id, entry);

      // ファイルシステムに保存
      await this.saveEntry(entry);

      // インデックスファイルを更新
      await this.saveIndexFile();

      // Gitにコミット
      const commitSha = await this.commitChanges(`Add vector entry: ${id}`);
      entry.commitSha = commitSha;

      return id;
    } catch (error) {
      console.error('Failed to add vector:', error);
      throw error;
    }
  }

  /**
   * ベクトル検索
   * @param query クエリベクトル
   * @param k 返す結果数
   * @returns 検索結果
   */
  async search(query: number[], k: number = 5): Promise<GitSearchResult[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const queryVector = new Float32Array(query);
      const results = this.index.search(queryVector, k);

      const searchResults: GitSearchResult[] = [];
      const entriesArray = Array.from(this.entries.values());

      for (let i = 0; i < results.keys.length; i++) {
        const index = Number(results.keys[i]);
        const score = results.distances[i];

        if (index < entriesArray.length) {
          const entry = entriesArray[index];
          searchResults.push({
            document: entry.document,
            score: 1 - score, // cosine similarityに変換
            id: entry.id,
            commitSha: entry.commitSha,
          });
        }
      }

      return searchResults;
    } catch (error) {
      console.error('Failed to search vectors:', error);
      throw error;
    }
  }

  /**
   * エントリーをファイルシステムに保存
   */
  private async saveEntry(entry: GitVectorStoreEntry): Promise<void> {
    try {
      // ベクトルデータを保存
      const vectorFilePath = `${this.vectorsDir}/${entry.id}.json`;
      const vectorData = {
        id: entry.id,
        vector: entry.vector,
        timestamp: entry.timestamp,
      };
      
      await this.gitFs.writeFile(vectorFilePath, JSON.stringify(vectorData, null, 2));

      // メタデータを保存
      const metadataFilePath = `${this.metadataDir}/${entry.id}.json`;
      const metadataData = {
        id: entry.id,
        document: {
          pageContent: entry.document.pageContent,
          metadata: entry.document.metadata,
        },
        timestamp: entry.timestamp,
      };
      
      await this.gitFs.writeFile(metadataFilePath, JSON.stringify(metadataData, null, 2));
    } catch (error) {
      console.error('Failed to save entry:', error);
      throw error;
    }
  }

  /**
   * インデックスファイルを保存
   */
  private async saveIndexFile(): Promise<void> {
    try {
      const indexData = {
        dimensions: this.dimensions,
        size: this.entries.size,
        timestamp: Date.now(),
        entries: Array.from(this.entries.values()).map(entry => ({
          id: entry.id,
          vector: entry.vector,
          document: {
            pageContent: entry.document.pageContent,
            metadata: entry.document.metadata,
          },
          timestamp: entry.timestamp,
          commitSha: entry.commitSha,
        })),
      };

      await this.gitFs.writeFile(this.indexFile, JSON.stringify(indexData, null, 2));
    } catch (error) {
      console.error('Failed to save index file:', error);
      throw error;
    }
  }

  /**
   * 変更をGitにコミット
   */
  private async commitChanges(message: string): Promise<string> {
    try {
      return await this.gitFs.commit(message);
    } catch (error) {
      console.error('Failed to commit changes:', error);
      throw error;
    }
  }

  /**
   * リモートにプッシュ
   */
  async push(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      await this.gitFs.push();
    } catch (error) {
      console.error('Failed to push to remote:', error);
      throw error;
    }
  }

  /**
   * リモートからプル
   */
  async pull(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      await this.gitFs.pull();
      
      // プル後にインデックスを再読み込み
      await this.loadExistingIndex();
    } catch (error) {
      console.error('Failed to pull from remote:', error);
      throw error;
    }
  }

  /**
   * インデックスサイズを取得
   */
  size(): number {
    return this.index.size();
  }

  /**
   * エントリーをIDで取得
   */
  getEntry(id: string): GitVectorStoreEntry | undefined {
    return this.entries.get(id);
  }

  /**
   * エントリーをIDで削除
   */
  async remove(id: string): Promise<boolean> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const entry = this.entries.get(id);
      if (!entry) {
        return false;
      }

      // エントリーを削除
      this.entries.delete(id);

      // ファイルを削除
      try {
        await this.gitFs.deleteFile(`${this.vectorsDir}/${id}.json`);
        await this.gitFs.deleteFile(`${this.metadataDir}/${id}.json`);
      } catch (error) {
        console.warn('Failed to remove entry files:', error);
      }

      // インデックスファイルを更新
      await this.saveIndexFile();

      // インデックスを再構築
      await this.rebuildIndex();

      // Gitにコミット
      await this.commitChanges(`Remove vector entry: ${id}`);

      return true;
    } catch (error) {
      console.error('Failed to remove entry:', error);
      throw error;
    }
  }

  /**
   * インデックスを再構築
   */
  private async rebuildIndex(): Promise<void> {
    try {
      // 新しいインデックスを作成
      this.index = new Index(this.dimensions);

      // エントリーを再追加
      let i = 0;
      for (const entry of this.entries.values()) {
        this.index.add(BigInt(i), new Float32Array(entry.vector));
        i++;
      }
    } catch (error) {
      console.error('Failed to rebuild index:', error);
      throw error;
    }
  }

  /**
   * すべてのエントリーをクリア
   */
  async clear(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      // インデックスをクリア
      this.index = new Index(this.dimensions);

      // エントリーをクリア
      this.entries.clear();

      // ファイルを削除
      const vectorFiles = await this.gitFs.readDir(this.vectorsDir);
      const metadataFiles = await this.gitFs.readDir(this.metadataDir);

      for (const file of vectorFiles) {
        if (file.endsWith('.json')) {
          await this.gitFs.deleteFile(`${this.vectorsDir}/${file}`);
        }
      }

      for (const file of metadataFiles) {
        if (file.endsWith('.json')) {
          await this.gitFs.deleteFile(`${this.metadataDir}/${file}`);
        }
      }

      // インデックスファイルを更新
      await this.saveIndexFile();

      // Gitにコミット
      await this.commitChanges('Clear all vector entries');
    } catch (error) {
      console.error('Failed to clear vector store:', error);
      throw error;
    }
  }

  /**
   * ブランチを切り替え
   * @param branchName ブランチ名
   * @param createIfNotExists 存在しない場合は作成
   */
  async switchBranch(branchName: string, createIfNotExists: boolean = false): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      await this.gitFs.switchBranch(branchName, createIfNotExists);
      
      // ブランチ切り替え後にインデックスを再読み込み
      await this.loadExistingIndex();
    } catch (error) {
      console.error('Failed to switch branch:', error);
      throw error;
    }
  }

  /**
   * 現在のブランチを取得
   */
  getCurrentBranch(): string {
    return this.gitFs.getCurrentBranch();
  }

  /**
   * ブランチ一覧を取得
   */
  async listBranches(): Promise<string[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    return this.gitFs.listBranches();
  }

  /**
   * コミット履歴を取得
   */
  async getCommitHistory(depth: number = 10): Promise<any[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    return this.gitFs.getCommitHistory(depth);
  }

  /**
   * リソースのクリーンアップ
   */
  async cleanup(): Promise<void> {
    try {
      await this.gitFs.cleanup();
      this.isInitialized = false;
    } catch (error) {
      console.error('Failed to cleanup GitVectorStore:', error);
      throw error;
    }
  }
}

export default GitVectorStore; 